import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, CheckCircle2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useVerifyEmail } from '@/hooks/auth/use-verify-email';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const verifyEmailMutation = useVerifyEmail();
  const token = searchParams.get('token') || '';

  // Trigger verify on mount if token is present
  React.useEffect(() => {
    if (token) {
      verifyEmailMutation.mutate({ token });
    }
  }, [token]);

  const isPending = verifyEmailMutation.isPending || verifyEmailMutation.isIdle;
  const isSuccess = verifyEmailMutation.isSuccess;
  const isError = verifyEmailMutation.isError;
  const errorMsg = (verifyEmailMutation.error as any)?.response?.data?.error?.message || 'The verification link may have expired or is invalid.';

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-bg-secondary p-6 relative overflow-hidden">
      {/* Decorative premium gradient background glow */}
      <div className="absolute top-[-10%] right-[-10%] w-[500px] h-[500px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-accent/5 blur-[120px] pointer-events-none" />

      <div className="w-full max-w-md z-10 space-y-6">
        {/* Brand logo display */}
        <div className="text-center">
          <span className="text-3xl font-extrabold tracking-tight text-primary">Cutlyft</span>
        </div>

        <Card className="border border-border/50 shadow-xl bg-bg-elevated/80 backdrop-blur-md">
          {/* Case 1: Token Missing */}
          {!token && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-error-bg text-error mb-3">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Invalid Verification Link</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  The email verification link is missing a valid token.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                Please click the link in your email exactly as it was sent, or request a new verification email from your profile settings.
              </CardContent>
            </>
          )}

          {/* Case 2: Verification In Progress */}
          {token && isPending && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center mb-3">
                  <Loader2 className="h-10 w-10 text-primary animate-spin" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Verifying Email</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  We are automatically verifying your email address with the backend...
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                This will only take a moment. Please keep this tab open.
              </CardContent>
            </>
          )}

          {/* Case 3: Verification Successful */}
          {token && isSuccess && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-success-bg text-success mb-3">
                  <CheckCircle2 className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Email Verified!</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  Your email address has been successfully verified.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-fg-muted">
                Your workspace is now fully activated. You are ready to start precision window fabrication!
              </CardContent>
              <CardFooter className="flex justify-center pt-2">
                <Button asChild className="w-full bg-primary hover:bg-primary/95 text-primary-foreground font-semibold h-11">
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              </CardFooter>
            </>
          )}

          {/* Case 4: Verification Failed */}
          {token && isError && (
            <>
              <CardHeader className="text-center">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-error-bg text-error mb-3">
                  <AlertCircle className="h-8 w-8" />
                </div>
                <CardTitle className="text-2xl text-fg-primary">Verification Failed</CardTitle>
                <CardDescription className="text-fg-secondary pt-1">
                  We encountered an error while verifying your email.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center text-sm text-error font-medium">
                {errorMsg}
              </CardContent>
            </>
          )}

          <CardFooter className="flex justify-center border-t border-border/30 pt-6">
            <Link to="/login" className="flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Login</span>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
